/**
 * What became of the intent the user just submitted. It never reports
 * (`api.getOrder` is silent), ends at "still open" rather than a failure when
 * the window runs out, and never invents a price: without the received amount
 * a fill shows none rather than the signed floor.
 */

import { useEffect, useRef, useState } from "react";
import { timing } from "../config";
import { api, type OrderFill, type OrderRecord } from "./api";
import { fromBaseUnits } from "./amount";
import { decimalsBySymbol } from "./tokens";

export const {
  fastPollMs: POLL_FAST_MS,
  slowAfterMs: BACKOFF_AFTER_MS,
  windowMs: OUTCOME_WINDOW_MS,
} = timing.outcome;

/** Over without settling. `filled` is terminal too, but carries amounts and has its own branch. */
const CLOSED_STATUSES = new Set(["cancelled", "canceled", "expired"]);

function normalizeStatus(status: string | undefined): string {
  return (status ?? "").toLowerCase().replace(/[\s-]+/g, "_");
}

/** The measured result of a settlement, in the two tokens' own base units. */
export interface OutcomeFill {
  /** Sell-token base units that actually left the maker. */
  soldBase: string;
  /** Buy-token base units actually received, or null when unindexed. */
  receivedBase: string | null;
  /**
   * What one sell token fetched, each side through its own decimals. Null
   * whenever `receivedBase` is.
   */
  executedRate: number | null;
  /**
   * Sell-token base units still open, "0" once done, null when not tracked.
   * Null renders as "remainder unknown", never as zero.
   */
  remainingBase: string | null;
  /** Most recent settlement transaction; null when none is indexed. */
  txHash: string | null;
}

export type OrderOutcome =
  /** Nothing submitted, or the slot was reset: the card shows nothing. */
  | { kind: "idle" }
  /** Submitted, no fill yet. `elapsedMs` is wall-clock since the POST landed. */
  | { kind: "pending"; elapsedMs: number }
  /**
   * Broadcast by a filler, not yet mined: the order was taken and what remains
   * is block time, so it must not read as waiting for a solver.
   */
  | { kind: "filling"; elapsedMs: number; txHash: string }
  | { kind: "filled"; fill: OutcomeFill }
  | { kind: "partial"; fill: OutcomeFill }
  /** Over without settling. `status` is the backend's own word, normalised. */
  | { kind: "closed"; status: string }
  /** Window elapsed, order still open and live. Not a failure. */
  | { kind: "unresolved" };

export interface OrderOutcomeInput {
  /** `null` disables the hook (no timers, no fetches): the card resets the slot that way. */
  orderHash: string | null;
  /** Symbol of the leg the maker sells; decimals come from runtime config. */
  sellSymbol: string;
  /** Symbol of the leg the maker receives. */
  buySymbol: string;
}

/**
 * Sell-token base units settled so far. `makingAmount - remainingMakingAmount`
 * comes first, as it stays right when a fill row lacks its own delta; the sum
 * of the fills is the fallback.
 */
function soldBaseUnits(record: OrderRecord, fills: readonly OrderFill[]): string | null {
  const making = record.order?.makingAmount;
  const remaining = record.remainingMakingAmount;
  if (typeof making === "string" && typeof remaining === "string") {
    try {
      const sold = BigInt(making) - BigInt(remaining);
      if (sold > 0n) return sold.toString();
      if (sold === 0n) return "0";
    } catch {
      // Non-numeric strings: fall through to the sum.
    }
  }
  let total = 0n;
  let seen = false;
  for (const fill of fills) {
    if (typeof fill.filledMakingAmount !== "string") continue;
    try {
      total += BigInt(fill.filledMakingAmount);
      seen = true;
    } catch {
      // ignore an unparseable row rather than losing the whole total
    }
  }
  return seen ? total.toString() : null;
}

/** Sum of a per-fill base-unit field; null when no row carries it. */
function sumFillField(fills: readonly OrderFill[], key: "filledTakingAmount"): string | null {
  let total = 0n;
  let seen = false;
  for (const fill of fills) {
    const value = fill[key];
    if (typeof value !== "string") continue;
    try {
      total += BigInt(value);
      seen = true;
    } catch {
      return null;
    }
  }
  return seen ? total.toString() : null;
}

/**
 * received ÷ sold, each through its own token's decimals first: dividing base
 * units would be off by 10^(sellDecimals - buyDecimals), a trillion for 18/6.
 */
function executedRate(
  soldBase: string,
  receivedBase: string,
  sellDecimals: number | null,
  buyDecimals: number | null,
): number | null {
  const sold = fromBaseUnits(soldBase, sellDecimals);
  const received = fromBaseUnits(receivedBase, buyDecimals);
  if (sold === null || received === null) return null;
  const soldNum = Number(sold);
  const receivedNum = Number(received);
  if (!Number.isFinite(soldNum) || !Number.isFinite(receivedNum) || soldNum === 0) return null;
  return receivedNum / soldNum;
}

/** Newest settlement transaction, ignoring cancel rows and unindexed hashes. */
function settlementTx(fills: readonly OrderFill[]): string | null {
  for (const fill of fills) {
    if (fill.kind !== undefined && fill.kind !== "fill") continue;
    if (typeof fill.txHash === "string" && fill.txHash !== "") return fill.txHash;
  }
  return null;
}

/**
 * One order record as a banner state, exported so its arithmetic is tested
 * without the timer loop.
 */
export function outcomeFromRecord(
  record: OrderRecord,
  sellSymbol: string,
  buySymbol: string,
  elapsedMs: number,
): OrderOutcome {
  const status = normalizeStatus(record.status);
  if (CLOSED_STATUSES.has(status)) {
    return { kind: "closed", status: status === "expired" ? "expired" : "cancelled" };
  }

  // Fills arrive newest-first; only settlements count towards amounts.
  const all = Array.isArray(record.fills) ? record.fills : [];
  const settlements = all.filter((f) => f.kind === undefined || f.kind === "fill");

  // No settled amount: nothing to report yet, whatever the status says.
  const soldBase = soldBaseUnits(record, settlements);
  if (soldBase === null || soldBase === "0") {
    const pending = record.pendingFill;
    return pending !== undefined && pending !== null && typeof pending.txHash === "string"
      ? { kind: "filling", elapsedMs, txHash: pending.txHash }
      : { kind: "pending", elapsedMs };
  }

  const receivedBase = sumFillField(settlements, "filledTakingAmount");
  const rate =
    receivedBase === null
      ? null
      : executedRate(
          soldBase,
          receivedBase,
          decimalsBySymbol(sellSymbol),
          decimalsBySymbol(buySymbol),
        );

  const remainingBase =
    typeof record.remainingMakingAmount === "string" ? record.remainingMakingAmount : null;
  const fill: OutcomeFill = {
    soldBase,
    receivedBase,
    executedRate: rate,
    remainingBase,
    txHash: settlementTx(settlements),
  };

  // An unknown remainder stays a partial: never claim a completion that cannot be seen.
  if (status === "filled" || remainingBase === "0") return { kind: "filled", fill };
  return { kind: "partial", fill };
}

/**
 * Watches one submitted order until it settles, is closed or the window runs
 * out. One 1 s ticker drives both the clock and the cadence; hidden-tab ticks
 * do nothing, and returning to the tab polls at once.
 */
export function useOrderOutcome({
  orderHash,
  sellSymbol,
  buySymbol,
}: OrderOutcomeInput): OrderOutcome {
  const [outcome, setOutcome] = useState<OrderOutcome>({ kind: "idle" });
  /** Latest symbols, so a re-render mid-flight never re-arms the timer. */
  const symbols = useRef({ sellSymbol, buySymbol });
  symbols.current = { sellSymbol, buySymbol };

  useEffect(() => {
    if (orderHash === null || orderHash === "") {
      setOutcome({ kind: "idle" });
      return;
    }

    const startedAt = Date.now();
    setOutcome({ kind: "pending", elapsedMs: 0 });

    let stopped = false;
    let inFlight = false;
    let lastPollAt = startedAt;
    /** Set by `visibilitychange` so the catch-up poll skips the cadence check. */
    let pollAsap = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const stop = () => {
      stopped = true;
      if (timer !== undefined) clearInterval(timer);
      timer = undefined;
    };

    const expired = () => Date.now() - startedAt >= timing.outcome.windowMs;

    /**
     * Hands over to the Orders page: a partial fill keeps its banner, anything
     * else is "still open".
     */
    const giveUp = () => {
      setOutcome((prev) => (prev.kind === "partial" ? prev : { kind: "unresolved" }));
      stop();
    };

    const poll = async () => {
      if (inFlight || stopped) return;
      inFlight = true;
      lastPollAt = Date.now();
      try {
        const record = await api.getOrder(orderHash);
        if (stopped) return;
        const next = outcomeFromRecord(
          record,
          symbols.current.sellSymbol,
          symbols.current.buySymbol,
          Date.now() - startedAt,
        );
        setOutcome(next);
        if (next.kind === "filled" || next.kind === "closed") {
          stop();
          return;
        }
        // Checked here, with a fresh answer, so "still open" never rests on a stale one.
        if (expired()) giveUp();
      } catch {
        // The next tick asks again. Past the deadline there is none, so one
        // failed final poll ends the watch.
        if (!stopped && expired()) giveUp();
      } finally {
        inFlight = false;
      }
    };

    const tick = () => {
      if (stopped) return;
      if (document.hidden) return;
      const elapsed = Date.now() - startedAt;
      setOutcome((prev) =>
        prev.kind === "pending" ? { kind: "pending", elapsedMs: elapsed } : prev,
      );
      const { fastPollMs, slowPollMs, slowAfterMs } = timing.outcome;
      const interval = elapsed >= slowAfterMs ? slowPollMs : fastPollMs;
      // The deadline forces a final poll, or a fill landing just before it would go unreported.
      if (pollAsap || expired() || Date.now() - lastPollAt >= interval) {
        pollAsap = false;
        void poll();
      }
    };

    const onVisibility = () => {
      if (stopped || document.hidden) return;
      pollAsap = true;
      tick();
    };

    timer = setInterval(tick, timing.tickMs);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // The symbols are read through the ref: restarting the watch would lose the elapsed clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHash]);

  return outcome;
}
